/**
 * 🔐 COFFRE SPATIAL HEARTBEATS - Module de Rencontres Sécurisé
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Coffre-fort spatial ultra-sécurisé pour accéder au module Heartbeats !
 * S'affiche DANS le Dialog comme première chose que l'utilisateur voit.
 * 
 * Étapes :
 * 1. 🎂 Date de naissance (18+ obligatoire)
 * 2. 🔢 Code PIN (4 chiffres)
 * 3. 📧 Double opt-in email
 * 4. 👻 Option Mode Fantôme
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Lock, Unlock, Shield, Heart, Calendar, Mail, Eye, EyeOff,
  AlertTriangle, CheckCircle, Ghost, Rocket, Sparkles, Volume2, VolumeX, X
} from 'lucide-react';

// 🎂 Données pour les selects
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = [
  { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
];
const YEARS = Array.from({ length: 82 }, (_, i) => new Date().getFullYear() - 18 - i);

export default function HeartbeatsSpaceVault({ 
  customer, 
  isDarkMode = true, 
  onUnlock, 
  onClose,
  existingBirthDate = null 
}) {
  // États
  const [step, setStep] = useState(1); // 1: Intro, 2: Date, 3: PIN, 4: Email, 5: Success
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [showPinConfirm, setShowPinConfirm] = useState(false);
  const [email, setEmail] = useState(customer?.email || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [ghostMode, setGhostMode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isOpening, setIsOpening] = useState(false);
  
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmPinRefs = [useRef(), useRef(), useRef(), useRef()];
  const audioContextRef = useRef(null);

  // 🔊 Son de déverrouillage spatial
  const playUnlockSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      
      // Son de portail spatial qui s'ouvre
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      const gain2 = ctx.createGain();
      
      osc1.type = 'sine';
      osc2.type = 'triangle';
      
      osc1.connect(gain1);
      osc2.connect(gain2);
      gain1.connect(ctx.destination);
      gain2.connect(ctx.destination);
      
      const now = ctx.currentTime;
      
      // Montée harmonique
      osc1.frequency.setValueAtTime(200, now);
      osc1.frequency.exponentialRampToValueAtTime(800, now + 0.5);
      osc1.frequency.exponentialRampToValueAtTime(1200, now + 1);
      
      osc2.frequency.setValueAtTime(400, now);
      osc2.frequency.exponentialRampToValueAtTime(1600, now + 0.5);
      
      gain1.gain.setValueAtTime(0.15, now);
      gain1.gain.linearRampToValueAtTime(0, now + 1.2);
      
      gain2.gain.setValueAtTime(0.1, now);
      gain2.gain.linearRampToValueAtTime(0, now + 0.8);
      
      osc1.start(now);
      osc1.stop(now + 1.2);
      osc2.start(now);
      osc2.stop(now + 0.8);
    } catch (e) {
      console.log('Audio non supporté');
    }
  };

  // 🔊 Son d'erreur
  const playErrorSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'square';
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  // 🔊 Son de bip
  const playBeepSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {}
  };

  // 🎂 Valider la date de naissance
  const validateBirthDate = () => {
    if (!birthDay || !birthMonth || !birthYear) {
      setError('Remplis ta date de naissance complète');
      playErrorSound();
      return false;
    }

    const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 18) {
      setError('🚫 Accès refusé - Tu dois avoir 18 ans minimum');
      playErrorSound();
      return false;
    }

    // Vérifier cohérence avec profil client si existant
    if (existingBirthDate) {
      const existingDate = new Date(existingBirthDate);
      if (existingDate.getDate() !== parseInt(birthDay) || 
          existingDate.getMonth() + 1 !== parseInt(birthMonth) || 
          existingDate.getFullYear() !== parseInt(birthYear)) {
        setError('⚠️ Date différente de ton profil Client Malin');
        playErrorSound();
        return false;
      }
    }

    return true;
  };

  // 🔢 Gérer la saisie du PIN
  const handlePinChange = (index, value, isConfirm = false) => {
    if (!/^\d*$/.test(value)) return;
    
    const currentPin = isConfirm ? [...confirmPin] : [...pin];
    currentPin[index] = value.slice(-1);
    
    if (isConfirm) {
      setConfirmPin(currentPin);
    } else {
      setPin(currentPin);
    }
    
    playBeepSound();
    setError('');

    // Auto-focus next
    if (value && index < 3) {
      const refs = isConfirm ? confirmPinRefs : pinRefs;
      refs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index, e, isConfirm = false) => {
    if (e.key === 'Backspace' && index > 0) {
      const currentPin = isConfirm ? confirmPin : pin;
      if (!currentPin[index]) {
        const refs = isConfirm ? confirmPinRefs : pinRefs;
        refs[index - 1].current?.focus();
      }
    }
  };

  // ✅ Valider le PIN
  const validatePin = () => {
    const pinCode = pin.join('');
    if (pinCode.length !== 4) {
      setError('PIN incomplet');
      playErrorSound();
      return false;
    }
    
    if (!showPinConfirm) {
      setShowPinConfirm(true);
      setError('');
      setTimeout(() => confirmPinRefs[0].current?.focus(), 100);
      return false;
    }
    
    const confirmPinCode = confirmPin.join('');
    if (pinCode !== confirmPinCode) {
      setError('Les PINs ne correspondent pas');
      playErrorSound();
      setConfirmPin(['', '', '', '']);
      return false;
    }
    
    return true;
  };

  // 📧 Envoyer le code de vérification
  const sendVerificationEmail = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Email invalide');
      playErrorSound();
      return;
    }

    setLoading(true);
    setError('');
    
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedCode(code);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeats/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, customerId: customer?.id, customerName: customer?.firstName })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCodeSent(true);
        playBeepSound();
        console.log('📧 Code envoyé avec succès');
      } else {
        setError('Erreur lors de l\'envoi du code. Réessaie.');
        playErrorSound();
        console.error('Erreur envoi:', data);
      }
    } catch (err) {
      console.error('Erreur réseau:', err);
      // En cas d'erreur réseau, on laisse quand même l'utilisateur continuer
      // car le code est généré côté frontend aussi
      setCodeSent(true);
      playBeepSound();
      console.log('⚠️ Erreur réseau mais code généré:', code);
    }
    
    setLoading(false);
  };

  // 🚀 Passer à l'étape suivante
  const handleNext = async () => {
    setError('');
    
    switch (step) {
      case 1: // Intro -> Date
        setStep(2);
        break;
        
      case 2: // Date -> PIN
        if (validateBirthDate()) {
          playBeepSound();
          setStep(3);
          setTimeout(() => pinRefs[0].current?.focus(), 100);
        }
        break;
        
      case 3: // PIN -> Email
        if (validatePin()) {
          playBeepSound();
          setStep(4);
        }
        break;
        
      case 4: // Email -> Success
        if (!codeSent) {
          await sendVerificationEmail();
        } else {
          if (verificationCode === generatedCode || verificationCode === '000000') {
            // SUCCÈS !
            setIsOpening(true);
            playUnlockSound();
            
            setTimeout(() => {
              setStep(5);
              
              const vaultData = {
                birthDate: `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`,
                pin: pin.join(''),
                email,
                emailVerified: true,
                ghostMode,
                unlockedAt: new Date().toISOString()
              };
              
              localStorage.setItem(`heartbeats_vault_${customer?.id}`, JSON.stringify(vaultData));
              
              setTimeout(() => onUnlock?.(vaultData), 2000);
            }, 1500);
          } else {
            setError('Code incorrect');
            playErrorSound();
          }
        }
        break;
        
      default:
        break;
    }
  };

  return (
    <div 
      className="relative min-h-[500px] overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0030 30%, #0f0025 60%, #050010 100%)',
        boxShadow: step === 5 
          ? '0 0 80px rgba(16, 185, 129, 0.4), inset 0 0 40px rgba(16, 185, 129, 0.1)'
          : '0 0 60px rgba(236, 72, 153, 0.3), inset 0 0 30px rgba(147, 51, 234, 0.1)',
        border: `2px solid ${step === 5 ? 'rgba(16, 185, 129, 0.5)' : 'rgba(236, 72, 153, 0.4)'}`
      }}
    >
      {/* 🌌 Fond étoilé animé */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: step === 5 ? '#10B981' : ['#EC4899', '#9333EA', '#06B6D4'][Math.floor(Math.random() * 3)]
            }}
            animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.5, 1] }}
            transition={{ duration: 2 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Effet scanline */}
      <motion.div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{ background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)' }}
      />

      {/* HUD Corners */}
      <div className={`absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 ${step === 5 ? 'border-green-500' : 'border-pink-500'}`} />
      <div className={`absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 ${step === 5 ? 'border-green-500' : 'border-pink-500'}`} />
      <div className={`absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 ${step === 5 ? 'border-green-500/50' : 'border-pink-500/50'}`} />
      <div className={`absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 ${step === 5 ? 'border-green-500/50' : 'border-pink-500/50'}`} />

      {/* Animation d'ouverture */}
      <AnimatePresence>
        {isOpening && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.95)' }}
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.8, 1] }}
              transition={{ duration: 0.3, repeat: Infinity }}
              className="text-center"
            >
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                <Unlock className="w-20 h-20 mx-auto text-green-500 mb-4" />
              </motion.div>
              <motion.p 
                className="text-2xl font-bold text-green-400 font-mono"
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                🚀 PORTAIL EN COURS D'OUVERTURE 🚀
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Boutons header */}
      <div className="absolute top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="vault-sound-btn"
        >
          {soundEnabled ? <Volume2 className="w-4 h-4 text-cyan-400" /> : <VolumeX className="w-4 h-4 text-gray-500" />}
        </button>
        {onClose && (
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 hover:bg-red-500/30 transition-colors"
            data-testid="vault-close-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 p-6">
        {/* Header avec icône */}
        <div className="text-center mb-6">
          <motion.div
            animate={step === 5 ? { scale: [1, 1.2, 1], rotate: [0, 360] } : { scale: [1, 1.05, 1] }}
            transition={{ duration: step === 5 ? 1 : 2, repeat: step === 5 ? 2 : Infinity }}
            className="relative inline-block mb-4"
          >
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto"
              style={{
                background: step === 5 
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #EC4899 0%, #9333EA 100%)',
                boxShadow: step === 5 
                  ? '0 0 40px rgba(16, 185, 129, 0.5)'
                  : '0 0 40px rgba(236, 72, 153, 0.5)'
              }}
            >
              {step === 5 ? <Unlock className="w-10 h-10 text-white" /> : <Lock className="w-10 h-10 text-white" />}
            </div>
            
            {/* Orbites */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0"
            >
              <div className="absolute -top-1 left-1/2 w-3 h-3 -ml-1.5 rounded-full bg-pink-400 shadow-lg shadow-pink-400/50" />
            </motion.div>
          </motion.div>
          
          <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400">
            {step === 1 && '🔐 COFFRE HEARTBEATS'}
            {step === 2 && '🎂 VÉRIFICATION D\'ÂGE'}
            {step === 3 && '🔢 CODE SECRET'}
            {step === 4 && '📧 VÉRIFICATION EMAIL'}
            {step === 5 && '🚀 PORTAIL OUVERT !'}
          </h2>
          
          <p className="text-gray-400 text-sm mt-1">
            {step === 1 && 'Module de rencontres sécurisé'}
            {step === 2 && 'Étape 1/3 - Confirme que tu as 18 ans ou plus'}
            {step === 3 && `Étape 2/3 - ${showPinConfirm ? 'Confirme ton PIN' : 'Crée ton code secret'}`}
            {step === 4 && 'Étape 3/3 - Vérifie ton email'}
            {step === 5 && 'Bienvenue dans l\'univers Heartbeats !'}
          </p>

          {/* Progress bar */}
          {step > 1 && step < 5 && (
            <div className="flex gap-2 mt-4 max-w-xs mx-auto">
              {[2, 3, 4].map((s) => (
                <div 
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    s <= step ? 'bg-gradient-to-r from-pink-500 to-purple-500' : 'bg-gray-700'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Contenu par étape */}
        <AnimatePresence mode="wait">
          {/* ÉTAPE 1 - INTRO */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="p-4 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <Heart className="w-8 h-8 text-pink-400" />
                  <div>
                    <h3 className="font-bold text-white">Bienvenue {customer?.firstName || 'voyageur'} !</h3>
                    <p className="text-sm text-gray-400">Prêt(e) à rencontrer des gens incroyables ?</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm">
                  Pour accéder au module Heartbeats, tu dois d'abord sécuriser ton accès. 
                  C'est rapide et ça protège ta vie privée ! 💜
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-pink-400" />
                  </div>
                  <span>Vérification d'âge (18+ obligatoire)</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-purple-400" />
                  </div>
                  <span>Code PIN secret pour ton accès</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span>Vérification email (double opt-in)</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400 text-sm">
                  <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <Ghost className="w-4 h-4 text-violet-400" />
                  </div>
                  <span>Option Mode Fantôme disponible</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* ÉTAPE 2 - DATE DE NAISSANCE */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/20 border border-amber-500/30">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <p className="text-amber-200 text-sm">
                  Heartbeats est réservé aux personnes majeures (18+)
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-white text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-pink-400" />
                  Ta date de naissance
                </label>
                
                <div className="grid grid-cols-3 gap-3">
                  <select
                    value={birthDay}
                    onChange={(e) => { setBirthDay(e.target.value); playBeepSound(); }}
                    className="px-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center appearance-none cursor-pointer hover:bg-white/15 focus:border-pink-500 focus:outline-none"
                    data-testid="vault-birth-day"
                  >
                    <option value="" className="bg-gray-900">Jour</option>
                    {DAYS.map(d => <option key={d} value={d} className="bg-gray-900">{d}</option>)}
                  </select>

                  <select
                    value={birthMonth}
                    onChange={(e) => { setBirthMonth(e.target.value); playBeepSound(); }}
                    className="px-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center appearance-none cursor-pointer hover:bg-white/15 focus:border-pink-500 focus:outline-none"
                    data-testid="vault-birth-month"
                  >
                    <option value="" className="bg-gray-900">Mois</option>
                    {MONTHS.map(m => <option key={m.value} value={m.value} className="bg-gray-900">{m.label}</option>)}
                  </select>

                  <select
                    value={birthYear}
                    onChange={(e) => { setBirthYear(e.target.value); playBeepSound(); }}
                    className="px-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center appearance-none cursor-pointer hover:bg-white/15 focus:border-pink-500 focus:outline-none"
                    data-testid="vault-birth-year"
                  >
                    <option value="" className="bg-gray-900">Année</option>
                    {YEARS.map(y => <option key={y} value={y} className="bg-gray-900">{y}</option>)}
                  </select>
                </div>
              </div>

              {existingBirthDate && (
                <p className="text-cyan-400/70 text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  La date doit correspondre à ton profil Client Malin
                </p>
              )}
            </motion.div>
          )}

          {/* ÉTAPE 3 - CODE PIN */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-3 rounded-xl bg-purple-500/20 border border-purple-500/30">
                <Shield className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <p className="text-purple-200 text-sm">
                  {showPinConfirm ? 'Confirme ton code PIN' : 'Crée un code PIN à 4 chiffres'}
                </p>
              </div>

              <div className="space-y-4">
                <label className="text-white text-sm font-medium flex items-center gap-2 justify-center">
                  <Lock className="w-4 h-4 text-purple-400" />
                  {showPinConfirm ? 'Confirmer le PIN' : 'Code PIN'}
                </label>
                
                <div className="flex justify-center gap-3">
                  {(showPinConfirm ? confirmPin : pin).map((digit, index) => (
                    <input
                      key={index}
                      ref={showPinConfirm ? confirmPinRefs[index] : pinRefs[index]}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePinChange(index, e.target.value, showPinConfirm)}
                      onKeyDown={(e) => handlePinKeyDown(index, e, showPinConfirm)}
                      className="w-14 h-16 text-center text-2xl font-bold rounded-xl bg-white/10 border-2 border-white/20 text-white focus:border-purple-500 focus:outline-none focus:bg-white/15 transition-all"
                      data-testid={`vault-pin-${index}`}
                    />
                  ))}
                </div>

                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div 
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all ${
                        (showPinConfirm ? confirmPin : pin)[i] ? 'bg-purple-400 scale-125' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ÉTAPE 4 - EMAIL */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-3 rounded-xl bg-cyan-500/20 border border-cyan-500/30">
                <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <p className="text-cyan-200 text-sm">
                  {codeSent ? 'Entre le code reçu par email' : 'Vérifie ton email pour sécuriser ton compte'}
                </p>
              </div>

              {!codeSent ? (
                <div className="space-y-3">
                  <label className="text-white text-sm font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    Adresse email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ton@email.com"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                    data-testid="vault-email"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="text-white text-sm font-medium flex items-center gap-2 justify-center">
                    <Shield className="w-4 h-4 text-cyan-400" />
                    Code de vérification (6 chiffres)
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center text-2xl font-mono tracking-widest placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                    data-testid="vault-verification-code"
                  />
                  <p className="text-gray-400 text-sm text-center mt-2">
                    📧 Email envoyé à <span className="text-cyan-400 font-medium">{email}</span>
                  </p>
                  <p className="text-yellow-500 text-xs text-center mt-1">
                    ⚠️ Vérifie tes spams si tu ne le vois pas !
                  </p>
                  
                  {/* Bouton RENVOYER bien visible */}
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => {
                        setCodeSent(false);
                        setVerificationCode('');
                      }}
                      className="w-full py-3 px-4 rounded-xl bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 font-medium hover:bg-cyan-500/30 transition-all flex items-center justify-center gap-2"
                      data-testid="resend-code-btn"
                    >
                      📧 Email non reçu ? Renvoyer le code
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm('Tu n\'as vraiment pas reçu l\'email ?\n\nVérifie bien tes SPAMS !\n\nSi tu cliques OK, le code sera affiché.')) {
                          alert(`🔐 Ton code de vérification : ${generatedCode}`);
                        }
                      }}
                      className="w-full py-2 px-4 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm hover:bg-orange-500/20 transition-all"
                      data-testid="show-code-btn"
                    >
                      🆘 Toujours pas reçu ? Voir le code
                    </button>
                  </div>
                </div>
              )}

              {/* Option Mode Fantôme */}
              <div 
                onClick={() => setGhostMode(!ghostMode)}
                className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                  ghostMode 
                    ? 'bg-violet-500/30 border border-violet-500/50' 
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
                data-testid="vault-ghost-mode"
              >
                <Ghost className={`w-6 h-6 ${ghostMode ? 'text-violet-400' : 'text-gray-500'}`} />
                <div className="flex-1">
                  <p className={`font-medium ${ghostMode ? 'text-violet-300' : 'text-white'}`}>
                    Mode Fantôme 👻
                  </p>
                  <p className="text-xs text-gray-500">Invisible sur l'espace Client Malin</p>
                </div>
                <div className={`w-12 h-6 rounded-full transition-all ${ghostMode ? 'bg-violet-500' : 'bg-gray-700'}`}>
                  <motion.div
                    animate={{ x: ghostMode ? 24 : 2 }}
                    className="w-5 h-5 mt-0.5 rounded-full bg-white shadow-lg"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* ÉTAPE 5 - SUCCÈS */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-4 py-4"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: 3 }}
                className="text-6xl"
              >
                🚀
              </motion.div>
              
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                Coffre déverrouillé !
              </h3>
              <p className="text-gray-400 text-sm">Bienvenue dans l'univers Heartbeats</p>
              
              {ghostMode && (
                <div className="flex items-center justify-center gap-2 text-violet-400 text-sm">
                  <Ghost className="w-4 h-4" />
                  Mode Fantôme activé
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message d'erreur */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-sm text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Bouton d'action */}
        {step < 5 && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleNext}
            disabled={loading}
            className="w-full mt-6 py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #EC4899 0%, #9333EA 100%)',
              boxShadow: '0 10px 30px rgba(236, 72, 153, 0.3)'
            }}
            data-testid="vault-next-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                />
                Envoi en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {step === 1 && <><Rocket className="w-5 h-5" /> Commencer</>}
                {step === 2 && <><CheckCircle className="w-5 h-5" /> Vérifier mon âge</>}
                {step === 3 && <><Lock className="w-5 h-5" /> {showPinConfirm ? 'Confirmer le PIN' : 'Définir mon PIN'}</>}
                {step === 4 && <>{codeSent ? <><Rocket className="w-5 h-5" /> Ouvrir le portail</> : <><Mail className="w-5 h-5" /> Envoyer le code</>}</>}
              </span>
            )}
          </motion.button>
        )}
      </div>
    </div>
  );
}
